import type { DeploymentLabel } from '../core/deploymentLabels'
import { templateSummary } from '../core/templateCatalog'
import { hostDisplayName } from '../core/hostName'
import type { Coin } from '../core/types'
import { depositFigures, escrowDseq, integerRate, sumCoins } from './deposits'
import type { CatalogHostDto, DomainLease, DomainLeaseList, EscrowDto, PendingDeployment } from './types'

/*
  The instances list's model: one row per deployment, joined from every source
  the tenant surface has, with each source degrading on its own.

  The shared grouping rule lives here too, so Overview and Instances count
  instances the same way.
*/

// ── Grouping (shared with Overview) ─────────────────────────────────────────

export interface GroupedInstance {
  id: string
  dseq: string
  hostName: string
  region: string
  state: DomainLease['state']
  running: boolean
  rate: Coin
}

export function statePriority(state: DomainLease['state']): number {
  switch (state) {
    case 'insufficient-funds':
      return 0
    case 'active':
      return 1
    case 'unknown':
      return 2
    case 'closed':
      return 3
  }
}

/** Leases by deployment, in first-seen order. A lease with no dseq keys on its own id. */
function leasesByDseq(leases: readonly DomainLease[]): Map<string, DomainLease[]> {
  const groups = new Map<string, DomainLease[]>()
  for (const lease of leases) {
    const key = lease.dseq && lease.dseq !== 'unknown' ? lease.dseq : lease.id || 'unknown'
    const group = groups.get(key) ?? []
    group.push(lease)
    groups.set(key, group)
  }
  return groups
}

/**
 * One deployment's state from all of its leases: any lease out of funds makes
 * the deployment out of funds; otherwise any unreadable lease makes it
 * unknown; otherwise any active lease makes it running; otherwise it is closed.
 */
function groupState(group: readonly DomainLease[]): DomainLease['state'] {
  if (group.some((item) => item.state === 'insufficient-funds')) return 'insufficient-funds'
  if (group.some((item) => item.state === 'unknown')) return 'unknown'
  if (group.some((item) => item.state === 'active')) return 'active'
  return 'closed'
}

/** The lease that speaks for the group: the most urgent one. */
function leadLease(group: readonly DomainLease[]): DomainLease {
  return [...group].sort((a, b) => statePriority(a.state) - statePriority(b.state))[0]!
}

export function groupInstances(leases: DomainLeaseList | null): GroupedInstance[] {
  if (!leases) return []
  return [...leasesByDseq(leases.leases).entries()]
    .map(([dseq, group]) => {
      const lease = leadLease(group)
      return {
        id: dseq,
        dseq,
        hostName: lease.provider.name,
        region: lease.provider.region,
        state: groupState(group),
        running: group.some((item) => item.state === 'active'),
        rate: lease.rate,
      }
    })
    .sort((a, b) => statePriority(a.state) - statePriority(b.state) || a.id.localeCompare(b.id))
}

// ── The instances list ──────────────────────────────────────────────────────

export type InstanceStatus = 'running' | 'out-of-funds' | 'waiting' | 'closed' | 'unknown'

export interface InstanceResources {
  cpuMilli: number
  memoryBytes: number
  storageBytes: number
  replicas: number
}

export interface InstanceRowModel {
  dseq: string
  /** `fallback` is "Deployment #<dseq>": nothing was recorded for it in this browser. */
  label: { name: string; source: 'recorded' | 'fallback' }
  /** Null when no image was recorded. "Personal LLM · from template" for an app. */
  image: string | null
  /** Created from a template with an app page: the row opens on the App tab. */
  appTab?: boolean
  /** Null for a request still waiting for offers: no host has it yet. */
  host: { name: string; region: string | null } | null
  resources: InstanceResources | null
  status: InstanceStatus
  /** Per block. The current rate while leases are open; the last known one once closed. */
  rate: Coin | null
  /** What a waiting request holds in escrow. */
  held: Coin | null
  /** The deposit's runway (`data/deposits.ts`), for rows a host is still serving. */
  runwayBlocks: number | null
  startedHeight: number | null
  endedHeight: number | null
}

export interface InstanceListInput {
  leases: DomainLeaseList | null
  pending: readonly PendingDeployment[]
  /** The raw `deployments/list` items; null when they could not be read. */
  deployments: readonly unknown[] | null
  escrow: EscrowDto | null
  catalogHosts: readonly CatalogHostDto[] | null
  labels: Record<string, DeploymentLabel>
  /** The chain height, for the deposit estimate. Without it the last settled balance is used. */
  height?: number | null | undefined
}

export const HOST_NOT_LISTED = 'Host not listed'

const STATUS_ORDER: Record<InstanceStatus, number> = { 'out-of-funds': 0, running: 1, waiting: 2, unknown: 3, closed: 4 }

function statusOf(state: DomainLease['state']): InstanceStatus {
  switch (state) {
    case 'insufficient-funds':
      return 'out-of-funds'
    case 'active':
      return 'running'
    case 'closed':
      return 'closed'
    case 'unknown':
      return 'unknown'
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

/** A `ResourceValue.val`: an integer, as the chain's string or a number. */
function quantity(value: unknown): number | null {
  const val = record(value)?.val
  const text = typeof val === 'number' ? String(val) : val
  if (typeof text !== 'string' || !/^\d+$/.test(text)) return null
  const number = Number(text)
  return Number.isSafeInteger(number) ? number : null
}

function deploymentDseq(item: unknown): string | null {
  const deployment = record(record(item)?.deployment) ?? record(item)
  const id = record(deployment?.id) ?? record(deployment?.deployment_id)
  const dseq = id?.dseq
  const text = typeof dseq === 'number' ? String(dseq) : dseq
  return typeof text === 'string' && /^\d+$/.test(text) ? text : null
}

/**
 * What one deployment asked for, from a `deployments/list` item.
 *
 * deployment v1beta4: `groups[].group_spec.resources[]` is a list of
 * `{ resource: { cpu.units.val, memory.quantity.val, storage[].quantity.val }, count }`
 * with CPU in millicores and sizes in bytes. One resource unit reports its own
 * size and `count` as the replicas. Several units (a multi-service SDL) have
 * no single per-replica size, so they are summed, count included, and reported
 * as one replica. Anything unreadable makes the whole answer null: a partial
 * sum would understate what the tenant is renting.
 */
export function parseDeploymentResources(item: unknown): InstanceResources | null {
  const groups = record(item)?.groups
  if (!Array.isArray(groups) || groups.length === 0) return null
  const units: Array<{ cpuMilli: number; memoryBytes: number; storageBytes: number; count: number }> = []
  for (const group of groups) {
    const resources = record(record(group)?.group_spec)?.resources
    if (!Array.isArray(resources) || resources.length === 0) return null
    for (const unit of resources) {
      const resource = record(record(unit)?.resource)
      const cpuMilli = quantity(record(resource?.cpu)?.units)
      const memoryBytes = quantity(record(resource?.memory)?.quantity)
      const storage = resource?.storage
      const volumes = Array.isArray(storage) ? storage.map((volume) => quantity(record(volume)?.quantity)) : null
      const count = record(unit)?.count
      if (cpuMilli === null || memoryBytes === null || !volumes || volumes.some((volume) => volume === null)) return null
      if (typeof count !== 'number' || !Number.isInteger(count) || count < 1) return null
      units.push({ cpuMilli, memoryBytes, storageBytes: volumes.reduce<number>((total, volume) => total + volume!, 0), count })
    }
  }
  if (units.length === 1) {
    const [unit] = units
    return { cpuMilli: unit!.cpuMilli, memoryBytes: unit!.memoryBytes, storageBytes: unit!.storageBytes, replicas: unit!.count }
  }
  return {
    cpuMilli: units.reduce((total, unit) => total + unit.cpuMilli * unit.count, 0),
    memoryBytes: units.reduce((total, unit) => total + unit.memoryBytes * unit.count, 0),
    storageBytes: units.reduce((total, unit) => total + unit.storageBytes * unit.count, 0),
    replicas: 1,
  }
}

function heightOf(dseq: string): number | null {
  const height = /^\d+$/.test(dseq) ? Number(dseq) : NaN
  return Number.isSafeInteger(height) && height > 0 ? height : null
}

function newestFirst(a: string, b: string): number {
  const left = /^\d+$/.test(a) ? BigInt(a) : 0n
  const right = /^\d+$/.test(b) ? BigInt(b) : 0n
  return left === right ? 0 : right > left ? 1 : -1
}

/** The recorded name for a deployment, or "Deployment #<dseq>" when this browser recorded none. */
export function labelFor(dseq: string, labels: Record<string, DeploymentLabel>): Pick<InstanceRowModel, 'label' | 'image' | 'appTab'> {
  const recorded = Object.prototype.hasOwnProperty.call(labels, dseq) ? labels[dseq] : undefined
  if (!recorded) return { label: { name: `Deployment #${dseq}`, source: 'fallback' }, image: null }
  // An app is named as the app, not by the image it happens to run first.
  const app = recorded.template ? templateSummary(recorded.template.id) : undefined
  return app?.app
    ? { label: { name: recorded.name, source: 'recorded' }, image: `${app.name} · from template`, appTab: true }
    : { label: { name: recorded.name, source: 'recorded' }, image: recorded.image }
}

/**
 * Every deployment the tenant has, one row each, sorted for attention:
 * out of funds, running, waiting, unknown, closed; newest first within each.
 */
export function buildInstanceList(input: InstanceListInput): InstanceRowModel[] {
  const hostsByOwner = new Map((input.catalogHosts ?? []).map((host) => [host.owner, host]))
  const resourcesByDseq = new Map<string, InstanceResources | null>()
  for (const item of input.deployments ?? []) {
    const dseq = deploymentDseq(item)
    if (dseq && !resourcesByDseq.has(dseq)) resourcesByDseq.set(dseq, parseDeploymentResources(item))
  }
  const escrowByDseq = new Map((Array.isArray(input.escrow?.accounts) ? input.escrow.accounts : [])
    .filter((account) => account && typeof account === 'object' && account.scope !== 'bid')
    .map((account) => [escrowDseq(account), account]))

  const leased: InstanceRowModel[] = [...leasesByDseq(input.leases?.leases ?? []).entries()].map(([dseq, group]) => {
    const lead = leadLease(group)
    const status = statusOf(groupState(group))
    const catalogHost = hostsByOwner.get(lead.provider.address)
    const active = group.filter((lease) => lease.state === 'active')
    // A missing price on any open lease makes the sum unavailable: a partial
    // sum would be a confident number that is too low.
    const rate = active.length > 0 ? sumCoins(active.map((lease) => integerRate(lease.rate))) : lead.rate
    const created = group.map((lease) => lease.createdHeight ?? null).filter((height): height is number => height !== null)
    const closed = group.map((lease) => lease.closedHeight ?? null).filter((height): height is number => height !== null)
    const account = escrowByDseq.get(dseq)
    return {
      dseq,
      ...labelFor(dseq, input.labels),
      host: catalogHost
        ? { name: hostDisplayName(catalogHost), region: catalogHost.region?.trim() || null }
        : { name: HOST_NOT_LISTED, region: null },
      resources: resourcesByDseq.get(dseq) ?? null,
      status,
      rate,
      held: null,
      runwayBlocks: (status === 'running' || status === 'out-of-funds') && account
        ? depositFigures(account, group, input.height ?? null).runwayBlocks
        : null,
      // The console creates the dseq at the chain height, so it is the
      // creation height when the lease does not report its own.
      startedHeight: created.length > 0 ? Math.min(...created) : heightOf(dseq),
      endedHeight: status === 'closed' && closed.length > 0 ? Math.max(...closed) : null,
    }
  })

  const leasedDseqs = new Set(leased.map((row) => row.dseq))
  const waiting: InstanceRowModel[] = input.pending
    .filter((item) => !leasedDseqs.has(item.dseq))
    .map((item) => ({
      dseq: item.dseq,
      ...labelFor(item.dseq, input.labels),
      host: null,
      resources: resourcesByDseq.get(item.dseq) ?? null,
      status: 'waiting',
      rate: null,
      held: escrowByDseq.get(item.dseq)?.balance ?? null,
      runwayBlocks: null,
      startedHeight: heightOf(item.dseq),
      endedHeight: null,
    }))

  return [...leased, ...waiting].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || newestFirst(a.dseq, b.dseq))
}
